import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ConsultCTA } from './consult-cta';

/**
 * ProgramCard - Display program information
 * @param {Object} props
 * @param {Object} props.program - Full program object from backend
 * @param {string} props.title - Program title (fallback)
 * @param {string} props.university - University name
 * @param {string} props.degree - Degree type (e.g., 'Bachelor', 'Master')
 * @param {string} props.duration - Program duration
 * @param {string} props.tuition - Tuition fee
 * @param {string} props.delivery - Delivery mode (e.g., 'On-Campus', 'Online')
 * @param {boolean} props.scholarshipsAvailable - Scholarships availability
 * @param {function} props.onConsultClick - Consult button click handler
 */
export const ProgramCard = React.memo(({
  program,
  title: titleProp,
  university: universityProp,
  degree: degreeProp,
  duration: durationProp,
  tuition: tuitionProp,
  delivery: deliveryProp,
  scholarshipsAvailable: scholarshipsProp = false,
  ctaText = "Learn more",
  onConsultClick,
}) => {
  const navigate = useNavigate();
  
  // Extract from program object if provided, fallback to direct props
  const title = program?.program_name || titleProp || 'Program';
  const university = program?.university_name || universityProp;
  const degree = program?.level_of_study || degreeProp;
  const duration = program?.duration || durationProp;
  const delivery = program?.mode_of_study || deliveryProp;
  const scholarshipsAvailable = program?.scholarships_available ?? scholarshipsProp;
  
  // Format tuition from backend
  const tuition = tuitionProp || (program?.estimated_fee_local 
    ? `${program.fee_currency_local || 'RM'} ${Number(program.estimated_fee_local).toLocaleString()}`
    : null);
  
  const handleClick = () => {
    if (onConsultClick) {
      onConsultClick();
    } else if (program?.slug) {
      navigate(`/programs/${program.slug}`);
    } else if (program?.id) {
      // Fallback to ID if slug not available (backward compatibility)
      navigate(`/programs/${program.id}`);
    }
  };
  
  return (
    <article
      data-testid="program-card"
      className="group bg-white border border-border rounded-card shadow-card hover:shadow-card-hover transition-shadow duration-200 overflow-hidden"
    >
      <div className="p-5">
        {/* Header - Program Name Most Prominent */}
        <div className="mb-3">
          <h3 className="font-bold text-base text-text mb-1.5 line-clamp-2">{title}</h3>
          {university && <p className="text-xs text-muted">{university}</p>}
        </div>

        {/* Meta Row - Not Dominating */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {degree && (
            <span className="inline-flex items-center px-2 py-0.5 rounded bg-indigo/5 text-indigo text-xs">
              {degree}
            </span>
          )}
          {duration && (
            <span className="text-xs text-muted">{duration}</span>
          )}
          {delivery && (
            <span className="text-xs text-muted">{delivery}</span>
          )}
        </div>

        {/* Fee - Tertiary */}
        {tuition && (
          <div className="mb-3">
            <p className="text-xs text-muted">Fee from</p>
            <p className="text-sm font-medium text-text">{tuition}</p>
          </div>
        )}

        {/* Scholarships Badge - Not Dominating */}
        {scholarshipsAvailable && (
          <div className="mb-4">
            <span className="inline-flex items-center text-xs px-2 py-0.5 rounded bg-emerald/5 text-emerald">
              Scholarships Available
            </span>
          </div>
        )}

        {/* CTA */}
        <div className="mt-4">
          <button 
            onClick={handleClick} 
            className="w-full px-4 py-2.5 text-sm font-semibold text-[#1E40AF] bg-[#EFF6FF] hover:bg-[#DBEAFE] rounded-lg transition-colors flex items-center justify-center gap-1"
          >
            {ctaText}
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </article>
  );
});